import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, Activity, PieChart, 
  ArrowUpRight, ArrowDownRight, Clock,
  DollarSign, Briefcase, ChevronRight, Printer,
  FileText, MessageSquare, Bell
} from 'lucide-react';
import InvestmentPrint from './admin/InvestmentPrint';
import {
  StatementsModal, AllInvestmentsModal, AnnouncementsModal,
  SupportTicketFormModal, SupportTicketListModal
} from './DashboardModals';

const StatCard = ({ label, value, trend, trendType = 'up' }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">{label}</p>
    <div className="flex items-end justify-between">
      <h3 className="text-3xl font-black text-white italic">{value}</h3>
      {trend && (
        <div className={`flex items-center gap-1 text-xs font-bold ${trendType === 'up' ? 'text-red-500' : 'text-rose-500'}`}>
          {trendType === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {trend}
        </div>
      )}
    </div>
  </div>
);

const Section = ({ title, icon: Icon, children, action }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
    <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-red-500/10 rounded-xl text-red-500">
          <Icon size={18} />
        </div>
        <h3 className="text-sm font-black uppercase tracking-widest text-slate-200">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const UnifiedDashboard = ({ data }) => {
  const { role, user, stats, recent_orders, recent_investments, recent_earnings, recent_ads, performance, all_investments, announcements } = data;
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [showStatements, setShowStatements] = useState(false);
  const [showAllInvestments, setShowAllInvestments] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [showSupportList, setShowSupportList] = useState(false);

  // Determine dashboard variant based on role
  const isInvestor = role === 'investor';
  const isDistributor = role === 'distributor' || role === 'super_distributor';
  const isAdvertiser = role === 'advertiser';

  const handlePrint = (inv) => {
    setShowStatements(false);
    setShowAllInvestments(false);
    setSelectedInvestment(inv);
  };

  const handleShowTickets = () => {
    setShowSupportList(true);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {selectedInvestment && (
        <InvestmentPrint 
          investment={selectedInvestment} 
          onClose={() => setSelectedInvestment(null)} 
        />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-4">
            <Activity size={12} /> {role.replace('_', ' ')} Panel
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white italic leading-tight">
            Welcome, <span className="text-red-500">{user?.name}</span>
          </h1>
          <p className="text-slate-400 mt-3 text-sm font-medium">
            Here's what's happening with your {role.replace('_', ' ')} account today.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/profile" className="px-6 py-3 rounded-2xl bg-slate-900 border border-slate-800 text-white font-bold text-sm hover:border-red-500 transition-all shadow-xl">
            Account Settings
          </Link>
          <button 
            onClick={() => setShowStatements(true)}
            className="px-6 py-3 rounded-2xl bg-red-600 text-white font-black text-sm hover:bg-red-700 transition-all shadow-lg shadow-red-900/40 flex items-center gap-2"
          >
            <FileText size={16} />
            View Statements
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {isInvestor && (
          <>
            <StatCard label="Total Invested" value={`₹${(stats.total_invested || 0).toLocaleString()}`} trend="+12.5%" />
            <StatCard label="Active Investments" value={stats.active_investments || 0} />
            <StatCard label="Total Returns" value={`₹${(stats.total_returns || 0).toLocaleString()}`} trend="+8.2%" />
            <StatCard label="Pending Payouts" value={`₹${(stats.pending_payouts || 0).toLocaleString()}`} trendType="down" trend="Based on growth" />
          </>
        )}
        {isDistributor && (
          <>
            <StatCard label="Total Commissions" value={`₹${(stats.total_commissions || 0).toLocaleString()}`} trend="+5.4%" />
            <StatCard label="Pending Approval" value={`₹${(stats.pending_commissions || 0).toLocaleString()}`} />
            <StatCard label="Last Month" value={`₹${(stats.last_month_earnings || 0).toLocaleString()}`} trend="+15%" />
            {role === 'super_distributor' && <StatCard label="Network Size" value={stats.network_size} trend="2 new this week" />}
          </>
        )}
        {isAdvertiser && (
          <>
            <StatCard label="Total Ads" value={stats.total_ads} />
            <StatCard label="Active Now" value={stats.active_ads} />
            <StatCard label="Total Impressions" value={(stats.total_impressions || 0).toLocaleString()} trend="+22%" />
            <StatCard label="Avg. CTR" value={`${stats.avg_ctr || performance?.avg_ctr || 0}%`} trend="+0.4%" />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Activity Section */}
        <div className="lg:col-span-2 space-y-8">
          {isInvestor && (
            <Section title="Portfolio Activity" icon={Briefcase} action={<button onClick={() => setShowAllInvestments(true)} className="text-[10px] font-black uppercase text-red-500 hover:text-red-400 transition">All Investments</button>}>
              <div className="space-y-4">
                {recent_investments?.length > 0 ? recent_investments.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-950/50 border border-slate-800/50 hover:border-red-500/30 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-red-500 font-bold border border-slate-800 group-hover:bg-red-500 group-hover:text-white transition-all">
                        INV
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Investment #{inv.investment_number || inv.id}</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase">{new Date(inv.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm font-black text-white">₹{inv.amount?.toLocaleString()}</p>
                        <span className={`text-[10px] font-black uppercase tracking-widest ${inv.status === 'active' ? 'text-red-500' : 'text-amber-500'}`}>
                          {inv.status}
                        </span>
                      </div>
                      <button 
                        onClick={() => setSelectedInvestment(inv)}
                        className="p-2.5 rounded-xl bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800 transition-all"
                        title="Download Certificate"
                      >
                        <Printer size={16} />
                      </button>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-12 text-slate-500 italic">No investment activity found.</div>
                )}
              </div>
            </Section>
          )}

          {isDistributor && (
            <Section title="Recent Earnings" icon={DollarSign} action={<button onClick={() => setShowStatements(true)} className="text-[10px] font-black uppercase text-red-500">Earnings Report</button>}>
              <div className="space-y-4">
                {recent_earnings?.length > 0 ? recent_earnings.map((earning) => (
                  <div key={earning.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-950/50 border border-slate-800/50 hover:border-red-500/30 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-red-500 font-bold border border-slate-800">
                        ₹
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Commission for Order #{earning.order_id}</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase">{new Date(earning.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-white">₹{earning.amount?.toLocaleString()}</p>
                      <span className="text-[10px] font-black uppercase tracking-widest text-red-500">
                        {earning.status}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-12 text-slate-500 italic">No earnings recorded yet.</div>
                )}
              </div>
            </Section>
          )}

          {isAdvertiser && (
            <Section title="Ad Performance" icon={TrendingUp} action={<Link to="/admin?tab=ads" className="text-[10px] font-black uppercase text-red-500">Create New Ad</Link>}>
              <div className="space-y-4">
                {recent_ads?.length > 0 ? recent_ads.map((ad) => (
                  <div key={ad.id} className="flex items-center justify-between p-4 rounded-2xl bg-slate-950/50 border border-slate-800/50 hover:border-red-500/30 transition-all group">
                    <div className="flex items-center gap-4">
                      {ad.image_url ? (
                        <img src={ad.image_url} alt="" className="w-12 h-12 rounded-xl object-cover border border-slate-800" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-blue-500 border border-slate-800">AD</div>
                      )}
                      <div>
                        <p className="text-sm font-bold text-white">{ad.title}</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase">{ad.position}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-white">{ad.clicks?.toLocaleString()} Clicks</p>
                      <span className="text-[10px] font-black uppercase tracking-widest text-red-500">
                        {ad.status}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-12 text-slate-500 italic">No ads running currently.</div>
                )}
              </div>
            </Section>
          )}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-8">
          <div className="bg-red-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl shadow-red-900/40">
            <h3 className="text-xl font-black italic mb-2">Platform Announcements</h3>
            <p className="text-red-50 opacity-80 text-sm leading-relaxed mb-6">
              {announcements && announcements.length > 0
                ? announcements[0]?.title || 'Latest updates available'
                : `Check out the new ${role.replace('_', ' ')} guidelines for Q3 2026 and our updated payout schedules.`}
            </p>
            <button 
              onClick={() => setShowAnnouncements(true)}
              className="flex items-center gap-2 bg-white text-red-600 px-6 py-3 rounded-xl font-black text-xs hover:bg-red-50 transition-all"
            >
              Read More <ChevronRight size={14} />
            </button>
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-100 mb-6 flex items-center gap-2">
              <Clock size={16} className="text-red-500" /> Support Tickets
            </h3>
            <div className="space-y-4">
              <button 
                onClick={handleShowTickets}
                className="w-full p-4 rounded-2xl bg-slate-950/50 border border-slate-800/50 hover:border-red-500/30 transition-all text-left"
              >
                <p className="text-xs font-bold text-slate-200">View My Tickets</p>
                <span className="text-[10px] font-black uppercase text-red-500 tracking-widest mt-1 block">Click to view all</span>
              </button>
              <button 
                onClick={() => setShowSupportForm(true)}
                className="w-full py-3 rounded-xl border border-dashed border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-500 transition-all text-xs font-bold flex items-center justify-center gap-2"
              >
                <MessageSquare size={14} />
                + Create Support Ticket
              </button>
            </div>
          </div>
        </div>
      </div>

      <StatementsModal show={showStatements} onClose={() => setShowStatements(false)} stats={stats} allInvestments={all_investments} recentInvestments={recent_investments} onPrint={handlePrint} />
      <AllInvestmentsModal show={showAllInvestments} onClose={() => setShowAllInvestments(false)} allInvestments={all_investments} recentInvestments={recent_investments} onPrint={handlePrint} />
      <AnnouncementsModal show={showAnnouncements} onClose={() => setShowAnnouncements(false)} announcements={announcements} />
      <SupportTicketFormModal show={showSupportForm} onClose={() => setShowSupportForm(false)} />
      <SupportTicketListModal show={showSupportList} onClose={() => setShowSupportList(false)} />
    </div>
  );
};

export default UnifiedDashboard;
